import i18n from "i18next";

/**
 * Interpolates DB template placeholders (#p1..#p5) with the recipient's params.
 * Used as a fallback when the i18n template for a notification id is missing.
 */
export function interpolateNotificationTemplate(template, params) {
  let result = template ?? "";
  const paramMap = {
    "#p1": params.param1,
    "#p2": params.param2,
    "#p3": params.param3,
    "#p4": params.param4,
    "#p5": params.param5,
  };
  for (const [placeholder, value] of Object.entries(paramMap)) {
    if (value != null) {
      // split/join instead of replaceAll — not polyfilled under CRA's browserslist
      result = result.split(placeholder).join(value);
    }
  }
  return result;
}

/**
 * Returns the { title, content, cta } for a notification recipient.
 * Primary source: the `notifications` i18n namespace (templates.{id}.* with
 * {{pN}} interpolation) so copy follows the app language. Falls back to the
 * raw DB template strings (#pN) when the i18n key doesn't exist.
 */
export function getInterpolatedNotification(notification, t) {
  const { notifications, param1, param2, param3, param4, param5 } =
    notification;
  const safeNotifications = notifications ?? {
    id: "",
    title: "",
    content: "",
    cta: "",
  };
  const notificationId = String(safeNotifications.id);
  const params = { param1, param2, param3, param4, param5 };
  const tParams = {
    p1: param1 ?? "",
    p2: param2 ?? "",
    p3: param3 ?? "",
    p4: param4 ?? "",
    p5: param5 ?? "",
  };

  if (t && i18n.exists(`notifications:templates.${notificationId}.title`)) {
    const cta = i18n.exists(`notifications:templates.${notificationId}.cta`)
      ? t(`templates.${notificationId}.cta`, tParams)
      : null;
    const cta2 = i18n.exists(`notifications:templates.${notificationId}.cta2`)
      ? t(`templates.${notificationId}.cta2`, tParams)
      : null;
    return {
      title: t(`templates.${notificationId}.title`, tParams),
      content: t(`templates.${notificationId}.content`, tParams),
      cta: cta || null,
      cta2: cta2 || null,
    };
  }

  return {
    title: interpolateNotificationTemplate(safeNotifications.title, params),
    content: interpolateNotificationTemplate(safeNotifications.content, params),
    cta: safeNotifications.cta
      ? interpolateNotificationTemplate(safeNotifications.cta, params)
      : null,
    cta2: null,
  };
}
